/**
 * Guided two-step flow for creating an animal group:
 *   Step 1 — name the group (creates the AnimalGroup).
 *   Step 2 — add the animals as stock classes (from the type dropdown), which
 *            pre-fill the group's feed budget.
 *
 * The feed budget is the single source of truth for animal numbers and stock
 * classes, so it is created/ensured automatically here — the farmer never has to
 * think about "creating a feed budget". On finish we save and land the user in the
 * animal group view with the (now prefilled) feed budget open.
 */

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Fade,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import { BottomSheetHeader, PageTitleBar } from "common/components";
import { useTranslation } from "common/locales";
import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { isOfflineWriteBlockedError } from "common/utils/offlineWriteGuard";
import type { StockClassInput } from "features/feedBudgetPlan/AddStockClassDialog";
import AddStockClassDialog from "features/feedBudgetPlan/AddStockClassDialog";
import { summariseStockClass } from "features/feedBudgetPlan/stockClassSummary";
import { useFeedBudgetPlan } from "features/feedBudgetPlan/useFeedBudgetPlan";
import type { AnimalGroup } from "model";
import { useCurrentFarm, useCurrentFarmAnimalGroups } from "model";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createAnimalGroup, deleteAnimalGroup } from "services";
import { useAppStore } from "stores";
import { analytics, AnalyticsEvents } from "../../common/analytics";

const GUID_TO_BE_CREATED = "GUID_TO_BE_CREATED";

const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

type Phase = "name" | "preparing" | "stock" | "building";

export default function AnimalGroupSetupWizard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { notify } = useNotification();

  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const { data: currentFarm } = useCurrentFarm();
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();

  const {
    plan,
    activePlanId,
    viewedPlanId,
    setViewedPlanId,
    isLoading,
    createPlan,
    ensureGroupBudget,
    removeGroupBudget,
    replicateGroupToOtherPlans,
    deletePlan,
    addStockClassToGroup,
    removeStockClassFromGroup,
    savePlan,
  } = useFeedBudgetPlan();

  const [phase, setPhase] = useState<Phase>("name");
  const [name, setName] = useState("");
  const [group, setGroup] = useState<AnimalGroup | null>(null);
  const [creating, setCreating] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const nameInputRef = useRef<HTMLInputElement>(null);
  const prepStartedRef = useRef(false);
  const ensureStartedRef = useRef(false);
  const savePromiseRef = useRef<Promise<void> | null>(null);
  // True when this wizard session created the feed budget plan (vs. reusing an
  // existing one) — determines how much to clean up on discard.
  const createdPlanRef = useRef(false);

  useEffect(() => {
    if (phase === "name") nameInputRef.current?.focus();
  }, [phase]);

  const handleBackToFarm = () => {
    if (currentFarm?.guid) navigate(`/farm/${currentFarm.guid}`);
  };

  // Leaving step 2 discards the half-finished group: delete the group and clean up
  // its feed budget (drop the whole plan if we created it just for this group,
  // otherwise just remove this group's budget entry).
  async function handleDiscard() {
    if (!group || discarding) {
      handleBackToFarm();
      return;
    }
    setDiscarding(true);
    try {
      const budgetCount = plan?.animalGroupBudgets.length ?? 0;
      if (createdPlanRef.current && budgetCount <= 1 && viewedPlanId) {
        deletePlan(viewedPlanId);
      } else {
        await removeGroupBudget(group.guid);
      }
      await deleteAnimalGroup(group.guid);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.animalGroups.all,
      });
      if (currentFarm?.guid) {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.farms.detail(currentFarm.guid),
        });
      }
    } catch {
      // Best-effort cleanup — navigate away regardless.
    }
    handleBackToFarm();
  }

  // --- Step 1: create the animal group ---
  async function handleCreateGroup() {
    const trimmed = name.trim();
    if (!trimmed || !currentFarmId) return;
    setCreating(true);
    try {
      const newGroup = await createAnimalGroup({
        guid: GUID_TO_BE_CREATED,
        farm: currentFarmId,
        name: trimmed,
        postGrazingTarget: 1500,
        rotationLength: 20,
        stockClasses: [],
      });
      analytics.track(AnalyticsEvents.ANIMAL_GROUP_CREATED);
      setGroup(newGroup);
      setPhase("preparing");
    } catch (e) {
      if (!isOfflineWriteBlockedError(e)) {
        notify(t("animalGroupInput.boundary.error"), "error");
      }
      setCreating(false);
    }
  }

  // --- Preparing: ensure a feed budget plan with a budget for this group exists ---
  // Kick off plan creation (or point at the active plan) once the new group is in
  // the cache and the plans list has settled.
  useEffect(() => {
    if (phase !== "preparing" || !group) return;
    if (prepStartedRef.current) return;
    if (isLoading) return;
    // Wait until the freshly-created group is visible in the shared cache so that
    // createPlan's default budgets include it.
    if (!animalGroups.some((g) => g.guid === group.guid)) return;

    prepStartedRef.current = true;
    void (async () => {
      if (activePlanId) {
        // A plan already exists — view it; the effect below ensures the budget.
        setViewedPlanId(activePlanId);
      } else {
        // No plan yet — create one (it is activated automatically as the first plan).
        createdPlanRef.current = true;
        await createPlan();
      }
    })();
  }, [
    phase,
    group,
    isLoading,
    animalGroups,
    activePlanId,
    setViewedPlanId,
    createPlan,
  ]);

  // Preparing -> stock: wait for the viewed plan to load, ensure it has a budget
  // for this group, then advance.
  useEffect(() => {
    if (phase !== "preparing" || !group) return;
    if (!plan || plan.guid !== viewedPlanId) return;

    const hasBudget = plan.animalGroupBudgets.some(
      (b) => b.animalGroupId === group.guid
    );
    if (hasBudget) {
      setPhase("stock");
      return;
    }

    if (ensureStartedRef.current) return;
    ensureStartedRef.current = true;
    void ensureGroupBudget(group.guid, group.name);
  }, [phase, group, plan, viewedPlanId, ensureGroupBudget]);

  // --- Step 2 data, derived from the plan (single source of truth) ---
  const groupBudget = plan?.animalGroupBudgets.find(
    (b) => b.animalGroupId === group?.guid
  );
  const stockClassPlans = groupBudget?.stockClassPlans ?? [];
  const currentMonth = new Date().getMonth();

  function handleAddClasses(classes: StockClassInput[]) {
    if (!group) return;
    classes.forEach((c) => {
      addStockClassToGroup(
        group.guid,
        c.name,
        c.defaultStockClassName,
        c.initialValues
      );
    });
  }

  function handleRemove(stockClassId: string) {
    if (!group) return;
    void removeStockClassFromGroup(group.guid, stockClassId);
  }

  // Per-month feed demand (kgDM/day) for the group, summed across stock classes.
  // Drives the "building" reveal chart — the preset literally forming the budget.
  const monthlyDemand = useMemo(() => {
    return MONTH_LABELS.map((_, m) =>
      stockClassPlans.reduce((sum, sc) => {
        const e =
          sc.months.find((x) => x.month === m && (x.yearOffset ?? 0) === 0) ??
          sc.months[m] ??
          sc.months[0];
        if (!e) return sum;
        return (
          sum +
          (e.animalCount *
            (e.averageWeightKg ?? 0) *
            (e.allocationPercent ?? 0)) /
            100
        );
      }, 0)
    );
  }, [stockClassPlans]);

  const totalDailyDemand = Math.round(monthlyDemand[currentMonth] ?? 0);
  const maxMonthlyDemand = Math.max(...monthlyDemand, 1);

  function handleFinish() {
    if (!group || stockClassPlans.length === 0) return;
    const groupGuid = group.guid;
    // Show the "preset becomes the feed budget" reveal and save in the background.
    // We rest on this summary — the user advances by tapping "View feed budget".
    setPhase("building");
    // Save the active plan, then copy this group's budget onto every other feed
    // budget so the new group appears in all of them, not just the active one.
    savePromiseRef.current = savePlan()
      .then(() => replicateGroupToOtherPlans(groupGuid))
      .catch((e) => {
        if (!isOfflineWriteBlockedError(e)) {
          notify(t("animalGroupInput.boundary.error"), "error");
        }
      });
  }

  async function handleViewBudget() {
    if (!group || navigating) return;
    setNavigating(true);
    // Ensure the plan finished saving before we open it on the detail page.
    await savePromiseRef.current;
    navigate(`/animal-group/${group.guid}?openFeedBudget=true`);
  }

  const headerTitle =
    phase === "name"
      ? t("animalGroupSetup.step1.title")
      : t("animalGroupSetup.step2.title");

  const onBack =
    phase === "name"
      ? handleBackToFarm
      : phase === "building"
      ? navigating
        ? undefined // mid-navigation — lock it
        : () => setPhase("stock") // back to editing the animals
      : () => void handleDiscard();

  return (
    <Box
      sx={{
        backgroundColor: isMobile ? "white" : "transparent",
        // On mobile this route renders over the map; cover the whole screen so the
        // map isn't visible behind the wizard (matches the full-screen route layer).
        ...(isMobile
          ? {
              position: "fixed",
              inset: 0,
              height: "100dvh",
              overflowY: "auto",
              zIndex: 1300,
              paddingBottom: "90px",
            }
          : { minHeight: "100%" }),
      }}
    >
      {isMobile ? (
        <BottomSheetHeader title={headerTitle} onBack={onBack} />
      ) : (
        <PageTitleBar title={headerTitle} onBack={onBack} />
      )}

      <Container maxWidth="sm" sx={{ pt: 2 }}>
        {/* ===== STEP 1: NAME ===== */}
        {phase === "name" && (
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {t("animalGroupSetup.step1.subtitle")}
            </Typography>
            <TextField
              required
              fullWidth
              inputRef={nameInputRef}
              label={t("edit.animalGroupName.label")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) void handleCreateGroup();
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Button color="secondary" onClick={handleBackToFarm}>
                {t("edit.button.cancel")}
              </Button>
              <Button
                variant="contained"
                disabled={!name.trim() || creating}
                onClick={() => void handleCreateGroup()}
                sx={{ ml: 1 }}
              >
                {creating ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t("animalGroupSetup.step1.continue")
                )}
              </Button>
            </Box>
          </Box>
        )}

        {/* ===== PREPARING ===== */}
        {phase === "preparing" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              py: 6,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("animalGroupSetup.preparing")}
            </Typography>
          </Box>
        )}

        {/* ===== STEP 2: ADD ANIMALS ===== */}
        {phase === "stock" && group && (
          <Box>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
              {t("animalGroupSetup.step2.subtitle")}
            </Typography>

            {/* Added stock classes */}
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                overflow: "hidden",
                mb: 2,
              }}
            >
              {stockClassPlans.length === 0 ? (
                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.secondary", textAlign: "center" }}
                  >
                    {t("animalGroupSetup.step2.empty")}
                  </Typography>
                </Box>
              ) : (
                stockClassPlans.map((sc, i) => {
                  const info = summariseStockClass(sc.months, currentMonth);
                  return (
                    <Box
                      key={sc.stockClassId}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 2,
                        py: 1.25,
                        borderTop: i === 0 ? "none" : "1px solid #F0F0F0",
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, minWidth: 36 }}>
                        {info.count}
                      </Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ lineHeight: 1.3 }}>
                          {sc.stockClassName}
                        </Typography>
                        {info.bornMonth !== null ? (
                          <Typography
                            variant="caption"
                            sx={{ color: "#5E3F87", fontWeight: 600 }}
                          >
                            {t("animalGroupSetup.fromMonth", {
                              month: MONTH_LABELS[info.bornMonth],
                            })}{" "}
                            · {info.allocationPercent.toFixed(1)}% BW
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary" }}
                          >
                            {info.allocationPercent.toFixed(1)}% BW ·{" "}
                            {info.demand} kgDM/day
                          </Typography>
                        )}
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={t("animalGroupSetup.step2.remove")}
                        onClick={() => handleRemove(sc.stockClassId)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              )}
            </Box>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddCircleOutlineIcon />}
              onClick={() => setAddDialogOpen(true)}
              sx={{ textTransform: "none" }}
            >
              {t("animalGroupSetup.step2.addAnimalType")}
            </Button>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                disabled={stockClassPlans.length === 0}
                onClick={() => void handleFinish()}
              >
                {t("animalGroupSetup.step2.finish")}
              </Button>
            </Box>

            <AddStockClassDialog
              open={addDialogOpen}
              onClose={() => setAddDialogOpen(false)}
              onAdd={handleAddClasses}
              planStartDate={plan?.startDate}
              planEndDate={plan?.endDate}
            />
          </Box>
        )}

        {/* ===== BUILDING: preset becomes the feed budget ===== */}
        {phase === "building" && group && (
          <Box
            sx={{
              "@keyframes scwGrowBar": {
                from: { transform: "scaleY(0)" },
                to: { transform: "scaleY(1)" },
              },
              "@keyframes scwRise": {
                from: { opacity: 0, transform: "translateY(8px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
              "@keyframes scwPop": {
                "0%": { opacity: 0, transform: "scale(0)" },
                "60%": { opacity: 1, transform: "scale(1.25)" },
                "100%": { opacity: 1, transform: "scale(1)" },
              },
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", mb: 2, textAlign: "center" }}
            >
              {t("animalGroupSetup.building.caption")}
            </Typography>

            {/* The stock classes the farmer entered — each gets a green check */}
            <Box
              sx={{
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                overflow: "hidden",
                mb: 1.5,
              }}
            >
              {stockClassPlans.map((sc, i) => {
                const info = summariseStockClass(sc.months, currentMonth);
                return (
                  <Box
                    key={sc.stockClassId}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 2,
                      py: 1.25,
                      borderTop: i === 0 ? "none" : "1px solid #F0F0F0",
                      opacity: 0,
                      animation: "scwRise 0.4s ease-out forwards",
                      animationDelay: `${i * 140}ms`,
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, minWidth: 36 }}>
                      {info.count}
                    </Typography>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ lineHeight: 1.3 }}>
                        {sc.stockClassName}
                      </Typography>
                      {info.bornMonth !== null ? (
                        <Typography
                          variant="caption"
                          sx={{ color: "#5E3F87", fontWeight: 600 }}
                        >
                          {t("animalGroupSetup.fromMonth", {
                            month: MONTH_LABELS[info.bornMonth],
                          })}{" "}
                          · {info.allocationPercent.toFixed(1)}% BW
                        </Typography>
                      ) : (
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          {info.allocationPercent.toFixed(1)}% BW ·{" "}
                          {info.demand} kgDM/day
                        </Typography>
                      )}
                    </Box>
                    <CheckCircleRoundedIcon
                      sx={{
                        color: "#43A047",
                        fontSize: 20,
                        transformOrigin: "center",
                        opacity: 0,
                        animation:
                          "scwPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
                        animationDelay: `${i * 140 + 260}ms`,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            {/* They sum into the group's daily feed demand */}
            <Box
              sx={{
                textAlign: "center",
                mb: 1.5,
                opacity: 0,
                animation: "scwRise 0.4s ease-out forwards",
                animationDelay: `${stockClassPlans.length * 140 + 200}ms`,
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {t("animalGroupSetup.building.demandLabel")}
              </Typography>
              <Typography
                sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#1a1a2e" }}
              >
                {totalDailyDemand}{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "text.secondary" }}
                >
                  kgDM/day
                </Typography>
              </Typography>
            </Box>

            {/* ...which becomes the feed budget across the year */}
            <Fade
              in
              style={{
                transitionDelay: `${stockClassPlans.length * 140 + 450}ms`,
              }}
            >
              <Box
                sx={{
                  border: "1px solid #D3C9E7",
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "white",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    display: "block",
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  {t("animalGroupSetup.building.budgetLabel")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 0.5,
                    height: 72,
                  }}
                >
                  {monthlyDemand.map((_, i) => {
                    // Start the calendar at the current month and wrap around.
                    const m = (currentMonth + i) % 12;
                    const d = monthlyDemand[m];
                    const isCurrent = i === 0;
                    return (
                      <Box
                        key={m}
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          gap: 0.25,
                          height: "100%",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            height: `${Math.max(
                              8,
                              (d / maxMonthlyDemand) * 100
                            )}%`,
                            borderRadius: "3px 3px 0 0",
                            background: isCurrent
                              ? "linear-gradient(180deg, #9575CD 0%, #5E3F87 100%)"
                              : "linear-gradient(180deg, #66BB6A 0%, #388E3C 100%)",
                            transformOrigin: "bottom",
                            transform: "scaleY(0)",
                            animation: "scwGrowBar 0.45s ease-out forwards",
                            animationDelay: `${
                              stockClassPlans.length * 140 + 550 + i * 55
                            }ms`,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "0.55rem",
                            fontWeight: isCurrent ? 700 : 400,
                            color: isCurrent ? "#5E3F87" : "text.secondary",
                          }}
                        >
                          {MONTH_LABELS[m][0]}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Fade>

            <Button
              fullWidth
              variant="contained"
              onClick={() => void handleViewBudget()}
              disabled={navigating}
              sx={{
                mt: 3,
                textTransform: "none",
                opacity: 0,
                animation: "scwRise 0.4s ease-out forwards",
                animationDelay: `${stockClassPlans.length * 140 + 1100}ms`,
              }}
            >
              {navigating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("animalGroupSetup.building.viewBudget")
              )}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
